import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, X, Plus, Minus, Calendar, Code, 
  Layers, Euro, Clock, ArrowRight, Download, Mail 
} from 'lucide-react';
import Button from '@/components/ui/Button';
import { EstimationResult as EstimationResultType, EstimationOption, Platform, RevenueModel, ProjectDescription, SelectedFunctionality } from '@/types/estimation';
import { AVAILABLE_OPTIONS, calculateEstimation } from '@/lib/estimationCalculator';

interface EstimationResultProps {
  initialData: {
    platforms: Platform[];
    revenueModels: RevenueModel[];
    projectDescription: ProjectDescription;
    selectedFunctionalities: SelectedFunctionality[];
  };
  onReset: () => void;
}

const EstimationResult: React.FC<EstimationResultProps> = ({ initialData, onReset }) => {
  const [selectedOptions, setSelectedOptions] = useState<EstimationOption[]>([]);

  const estimation = useMemo(() => {
    if (!initialData?.revenueModels || !initialData?.platforms) {
      // Return a default estimation if data is missing
      return null;
    }
    try {
      return calculateEstimation(
        initialData.platforms || [],
        initialData.revenueModels || [],
        initialData.projectDescription || { description: '', targetUsers: '' },
        initialData.selectedFunctionalities || [],
        selectedOptions
      );
    } catch (error) {
      console.error('Error calculating estimation:', error);
      return null;
    }
  }, [initialData, selectedOptions]);

  // Show loading or error state if estimation is null
  if (!estimation) {
    return (
      <div className="w-full max-w-6xl mx-auto text-center py-12">
        <p className="text-slate-600 mb-4">Erreur lors du calcul de l'estimation.</p>
        <Button onClick={onReset}>Recommencer</Button>
      </div>
    );
  }

  const toggleOption = (option: EstimationOption) => {
    setSelectedOptions(prev => {
      const exists = prev.find(o => o.id === option.id);
      if (exists) {
        return prev.filter(o => o.id !== option.id);
      } else {
        return [...prev, option];
      }
    });
  };

  const isOptionSelected = (optionId: string) => {
    return selectedOptions.some(o => o.id === optionId);
  };

  // Filter options based on what's already included
  const availableOptions = AVAILABLE_OPTIONS.filter(option => {
    // Don't show platform options if already selected
    if (option.category === 'platform') {
      if (option.id === 'mobile-app' && (estimation.platforms.includes('mobile') || estimation.platforms.includes('both'))) {
        return false;
      }
      if (option.id === 'web-app' && (estimation.platforms.includes('web') || estimation.platforms.includes('both'))) {
        return false;
      }
    }
    return true;
  });

  const groupedOptions = useMemo(() => {
    const groups: Record<string, EstimationOption[]> = {
      feature: [],
      service: [],
      platform: [],
    };
    availableOptions.forEach(option => {
      groups[option.category].push(option);
    });
    return groups;
  }, [availableOptions]);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center pt-12 md:pt-16"
      >
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Votre estimation personnalisée
        </h2>
        <p className="text-lg text-slate-600">
          Voici le détail de votre projet avec les prix ajustés selon vos sélections
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Price Summary Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-orcsys-500 to-orcsys-600 rounded-2xl p-8 text-white"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-orcsys-100 mb-2">Estimation totale</p>
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold">{estimation.totalPrice.toLocaleString()}</span>
                  <span className="text-2xl ml-2 text-orcsys-100">€</span>
                </div>
                <p className="text-orcsys-100 mt-2">
                  Fourchette: {estimation.minPrice.toLocaleString()}€ - {estimation.maxPrice.toLocaleString()}€
                </p>
              </div>
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                <Euro className="w-10 h-10" />
              </div>
            </div>
            <div className="flex gap-4">
              <Button
                variant="outline"
                className="bg-white text-orcsys-600 hover:bg-white/90 flex-1"
                onClick={() => {
                  // TODO: Implement download PDF
                  console.log('Download PDF');
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
              <Button
                variant="outline"
                className="bg-white text-orcsys-600 hover:bg-white/90 flex-1"
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <Mail className="w-4 h-4 mr-2" />
                Nous contacter
              </Button>
            </div>
          </motion.div>

          {/* Project Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Layers className="w-6 h-6 mr-2 text-orcsys-500" />
              Détails du projet
            </h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Description</p>
                <p className="text-slate-700">{estimation.projectDescription.description}</p>
              </div>
              
              {estimation.projectDescription.targetUsers && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Utilisateurs cibles</p>
                  <p className="text-slate-700">{estimation.projectDescription.targetUsers}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-slate-500 mb-2">Modèles de revenus</p>
                <div className="flex flex-wrap gap-2">
                  {estimation.revenueModels.map((model, index) => {
                    const labels: Record<string, string> = {
                      subscription: 'Abonnement',
                      'in-app-purchase': 'Achats in-app',
                      ads: 'Publicités',
                      commission: 'Commission',
                      'one-time-payment': 'Paiement unique',
                      freemium: 'Freemium',
                      sponsorship: 'Sponsoring',
                      other: 'Autre',
                    };
                    return (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gradient-to-r from-orcsys-500 to-orcsys-600 text-white rounded-full text-sm font-medium"
                      >
                        {labels[model] || model}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Functionalities */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2 text-orcsys-500" />
              Fonctionnalités incluses
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {estimation.functionalities.map((func, index) => (
                <div key={index} className="flex items-center p-3 bg-slate-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-orcsys-500 mr-3 flex-shrink-0" />
                  <span className="text-slate-700">{func}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Technologies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Code className="w-6 h-6 mr-2 text-orcsys-500" />
              Technologies
            </h3>
            <div className="flex flex-wrap gap-3">
              {estimation.technologies.map((tech, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Timetable */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Clock className="w-6 h-6 mr-2 text-orcsys-500" />
              Planning estimé
            </h3>
            <div className="space-y-4">
              {estimation.timetable.phases.map((phase, index) => (
                <div key={index} className="flex items-start p-4 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-orcsys-500 text-white flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-slate-900">{phase.name}</h4>
                      <span className="text-sm text-orcsys-600 font-medium">{phase.duration}</span>
                    </div>
                    <p className="text-sm text-slate-600">{phase.description}</p>
                  </div>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-lg font-semibold text-slate-900">
                  Durée totale estimée: <span className="text-orcsys-500">{estimation.timetable.totalDuration}</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sidebar - Options */}
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-6 sticky top-8"
          >
            <h3 className="text-xl font-bold text-slate-900 mb-4">
              Options supplémentaires
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Ajoutez ou retirez des options pour personnaliser votre projet
            </p>

            <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
              {Object.entries(groupedOptions).map(([category, options]) => {
                if (options.length === 0) return null;
                return (
                  <div key={category}>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                      {category === 'feature' ? 'Fonctionnalités' : category === 'service' ? 'Services' : 'Plateformes'}
                    </h4>
                    <div className="space-y-2">
                      {options.map((option) => {
                        const isSelected = isOptionSelected(option.id);
                        return (
                          <motion.button
                            key={option.id}
                            onClick={() => toggleOption(option)}
                            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? 'border-orcsys-500 bg-orcsys-50'
                                : 'border-slate-200 hover:border-orcsys-200'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h5 className="font-semibold text-slate-900 text-sm mb-1">
                                  {option.name}
                                </h5>
                                <p className="text-xs text-slate-600">
                                  {option.description}
                                </p>
                              </div>
                              <div className={`ml-2 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected
                                  ? 'border-orcsys-500 bg-orcsys-500'
                                  : 'border-slate-300'
                              }`}>
                                {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-lg font-bold text-orcsys-500">
                                +{option.price.toLocaleString()}€
                              </span>
                              {isSelected && (
                                <span className="text-xs text-orcsys-600 font-medium">
                                  Inclus
                                </span>
                              )}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Selected Options Summary */}
            {selectedOptions.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Options sélectionnées</span>
                  <span className="text-lg font-bold text-orcsys-500">
                    +{selectedOptions.reduce((sum, opt) => sum + opt.price, 0).toLocaleString()}€
                  </span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Reset Button */}
          <Button
            variant="outline"
            onClick={onReset}
            className="w-full"
          >
            Recommencer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EstimationResult;

